import { mqMin, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, ContentUnavailable, IndeterminateProgressCircle, Typography } from '@sunwebgroup/ui-components/uds'
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useMount } from 'react-use'
import { styled } from 'styled-components'
import userProfileUseCases from '../domain/useCases'
import { userProfileStore } from '../infrastructure/store'
import userProfileApiClient from '../infrastructure/userProfileApiClient'
import { useUserProfileStore } from '../infrastructure/useUserProfileStore'
import { UserProfileEditForm, UserProfileField, UserProfileLayout } from './components'

const UserProfileInfoContainer = styled.div`
  background-color: ${tok('semantics_views_background_color')};
  padding: ${tokPx('primitives_space_20')};
  width: 100%;
  display: flex;
  gap: ${tokPx('primitives_space_16')};
  flex-direction: column;
  align-items: flex-start;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    border-radius: ${tokPx('semantics_boxes_corner_radius')};
  }
`
const UserProfileInfoContainerCenterContent = styled(UserProfileInfoContainer)`
  display: flex;
  align-items: center;
  padding: ${tokPx('primitives_space_64')};
  justify-content: center;
`

function UserProfileContent() {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const useCases = React.useMemo(
    () => userProfileUseCases({
      apiClient: userProfileApiClient,
      store: userProfileStore,
      invalidateUserQuery: () => queryClient.invalidateQueries({ queryKey: ['user'] }),
    }),
    [queryClient],
  )
  useMount(() => useCases.pageLoaded())
  const isLoading = useUserProfileStore(state => state.retrieveStatus.loading)
  const isError = useUserProfileStore(state => state.retrieveStatus.error)
  const firstName = useUserProfileStore(state => state.firstName)
  const lastName = useUserProfileStore(state => state.lastName)
  const birthDate = useUserProfileStore(state => state.birthDate)
  const birthDateUnformatted = useUserProfileStore(state => state.birthDateUnformatted)
  const isEditing = useUserProfileStore(state => state.editing)
  const setEditing = useUserProfileStore(state => state.setEditing)
  const isSaving = useUserProfileStore(state => state.saveStatus.loading)
  const isSaveSuccess = useUserProfileStore(state => state.saveStatus.success)
  const isSaveError = useUserProfileStore(state => state.saveStatus.error)

  const banner = isSaveError
    ? { type: 'danger' as const, text: intl.formatMessage({ id: 'user_profile_save_error' }) }
    : isSaveSuccess
      ? { type: 'success' as const, text: intl.formatMessage({ id: 'user_profile_save_success' }) }
      : undefined
  const fullName = `${firstName} ${lastName}`.trim()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setFocus,
  } = useForm({
    defaultValues: {
      firstName,
      lastName,
      birthDateUnformatted,
    },
  })

  React.useEffect(() => {
    if (isEditing) {
      setFocus('firstName')
    }
  }, [isEditing, setFocus])

  function handleEditClick() {
    reset({
      firstName,
      lastName,
      birthDateUnformatted,
    })
    setEditing(true)
  }

  async function onSubmit(data: { firstName: string, lastName: string, birthDateUnformatted: string }) {
    await useCases.updateProfile({
      firstName: data.firstName,
      lastName: data.lastName,
      birthDate: data.birthDateUnformatted,
    })
  }

  if (isLoading || isSaving) {
    return (
      <UserProfileLayout>
        <UserProfileInfoContainerCenterContent>
          <IndeterminateProgressCircle />
        </UserProfileInfoContainerCenterContent>
      </UserProfileLayout>
    )
  }

  if (isError || !fullName) {
    return (
      <UserProfileLayout>
        <UserProfileInfoContainerCenterContent>
          <Typography $variant="body-m-regular-tight">
            <ContentUnavailable
              text={intl.formatMessage({ id: 'user_profile_load_error' })}
              supportingText={intl.formatMessage({ id: 'user_profile_load_error_supporting_text' })}
              type="alert"
            />
          </Typography>
        </UserProfileInfoContainerCenterContent>
      </UserProfileLayout>
    )
  }

  return (
    <UserProfileLayout banner={banner}>
      <UserProfileInfoContainer>
        <Typography as="h2" $variant="display-h-regular">{intl.formatMessage({ id: 'user_profile_details_title' })}</Typography>
        {!isEditing
          ? (
              <>
                <UserProfileField
                  icon="Person"
                  label={intl.formatMessage({ id: 'user_profile_name_label' })}
                  value={fullName}
                />
                <UserProfileField
                  icon="Cake"
                  label={intl.formatMessage({ id: 'user_profile_birthday_label' })}
                  value={birthDate}
                />
                <Button
                  $type="secondary"
                  $size="XS"
                  onClick={handleEditClick}
                >
                  {intl.formatMessage({ id: 'user_profile_edit_button' })}
                </Button>
              </>
            )
          : (
              <UserProfileEditForm
                errors={errors}
                register={register}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                isSaving={isSaving}
                intl={intl}
              />
            )}
      </UserProfileInfoContainer>
    </UserProfileLayout>
  )
}

const queryClient = new QueryClient()

export function UserProfile() {
  return (
    <QueryClientProvider client={queryClient}>
      <UserProfileContent />
    </QueryClientProvider>
  )
}
